/**
 * Vista em tabela da lista de empresas (RNF15 / RNF16), para centenas de
 * linhas: cabecalho ordenavel, paginacao e total do recorte no rodape.
 *
 * Decisoes nao obvias:
 *  1. So a PAGINA chega aqui (`linhas`); o total do rodape soma o recorte
 *     inteiro (`recorte`), e diz isso -- o total nunca discorda do filtro.
 *  2. Cabecalho ordenavel e um botao dentro do `th` com `aria-sort`; o
 *     `title` explica a coluna em termos de negocio (o que entra no numero).
 *  3. Abaixo de `md` vira lista de cartoes compactos com as mesmas medidas
 *     (padrao de LeadsTable). Oito colunas em 360px nao cabem.
 *  4. Um `secondary` e um `ghost` por linha, nunca `primary`: dez botoes
 *     vermelhos numa tabela deixam de apontar a acao principal.
 *  5. O nome da empresa e LINK para o detalhe (abre em nova aba com o botao do
 *     meio); "Abrir CRM" e botao porque troca a empresa em foco antes.
 */

import { Link } from 'react-router-dom';
import { Pagination } from '../../../components/Pagination';
import { Sparkline } from '../../../components/Sparkline';
import { Badge, Button, CompanyLogo, LinkButton } from '../../../components/ui';
import { VALOR_AUSENTE, formatBRLFromCents, formatCount, formatPercentFromBps, formatRatio } from '../../../lib/format';
import type { LinhaEmpresa } from '../lista';
import { achadoPrincipal } from '../saude';
import type { ColunaLista, OrdemLista } from '../types';
import { COLUNA_INFO, MODELO_COBRANCA, PROVEDOR_INFO, STATUS_EMPRESA } from '../vocabulario';
import { Delta } from './Delta';
import { SeloDeSaude } from './SaudeEmpresa';

/** O percentual e o de HOJE; a comissao ao lado soma o que ja foi apurado, congelado por venda (ADR-001). */
const TITULO_PERCENTUAL = 'Percentual atual da empresa. A comissão ao lado já foi apurada e fica congelada no percentual de cada venda.';

function Cabecalho({
  coluna,
  ordem,
  onOrdenar,
  className = '',
}: {
  readonly coluna: ColunaLista;
  readonly ordem: OrdemLista;
  readonly onOrdenar: (coluna: ColunaLista) => void;
  readonly className?: string;
}): JSX.Element {
  const info = COLUNA_INFO[coluna];
  const ativa = ordem.coluna === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (ordem.desc ? 'descending' : 'ascending') : 'none'} className={`px-3 py-3 font-medium first:pl-5 ${info.numerica ? 'text-right' : ''} ${className}`}>
      <button
        type="button"
        onClick={() => onOrdenar(coluna)}
        title={info.titulo}
        className={`inline-flex items-center gap-1 rounded-md uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''} ${info.numerica ? 'flex-row-reverse' : ''}`}
      >
        {info.rotulo}
        <span aria-hidden="true" className={`text-[10px] ${ativa ? '' : 'opacity-40'}`}>
          {ativa ? (ordem.desc ? '▼' : '▲') : '↕'}
        </span>
      </button>
    </th>
  );
}

function Provedores({ linha }: { readonly linha: LinhaEmpresa }): JSX.Element {
  if (linha.provedores.length === 0) {
    return <span className="text-[11px] text-status-warning">Nenhum provedor conectado</span>;
  }
  return <span className="text-[11px] text-ink-subtle">{linha.provedores.map((p) => PROVEDOR_INFO[p].curto).join(' · ')}</span>;
}

function taxaDe(linha: LinhaEmpresa): string {
  const m = linha.item?.metrics ?? null;
  return m === null || m.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(m.recoveryRateBps);
}

function Tendencia({ linha }: { readonly linha: LinhaEmpresa }): JSX.Element {
  const valores = linha.item?.weeklyRecoveredCents ?? [];
  if (valores.length < 2) {
    return <span className="text-[11px] text-ink-subtle">Poucas semanas</span>;
  }
  const ultimo = valores[valores.length - 1] ?? 0;
  return <Sparkline values={valores} label={`Recuperado por semana de ${linha.company.name}; última semana ${formatBRLFromCents(ultimo)}`} className="h-8 w-24" />;
}

export function TabelaEmpresas({
  linhas,
  recorte,
  ordem,
  onOrdenar,
  offset,
  onOffset,
  tamanhoPagina,
  linkDe,
  onAbrirCrm,
}: {
  /** So a pagina atual. */
  readonly linhas: readonly LinhaEmpresa[];
  /** O recorte inteiro (todas as paginas), para o total e a paginacao. */
  readonly recorte: readonly LinhaEmpresa[];
  readonly ordem: OrdemLista;
  readonly onOrdenar: (coluna: ColunaLista) => void;
  readonly offset: number;
  readonly onOffset: (offset: number) => void;
  readonly tamanhoPagina: number;
  readonly linkDe: (companyId: string) => string;
  readonly onAbrirCrm: (companyId: string) => void;
}): JSX.Element {
  const soma = recorte.reduce(
    (acc, l) => ({
      recuperado: acc.recuperado + (l.item?.metrics.recoveredAmountCents ?? 0),
      comissao: acc.comissao + (l.item?.metrics.commissionCents ?? 0),
      vendas: acc.vendas + (l.item?.metrics.recoveredCount ?? 0),
      abandonos: acc.abandonos + (l.item?.metrics.abandonedCount ?? 0),
    }),
    { recuperado: 0, comissao: 0, vendas: 0, abandonos: 0 },
  );

  return (
    <section aria-label="Empresas em tabela" className="flex flex-col rounded-2xl border border-line bg-surface shadow-soft">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[68rem] border-collapse text-sm">
          <caption className="sr-only">Empresas com saúde, status, recuperado, comissão, taxa e tendência no período. Ordenável pelo cabeçalho.</caption>
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
              <Cabecalho coluna="empresa" ordem={ordem} onOrdenar={onOrdenar} />
              <Cabecalho coluna="saude" ordem={ordem} onOrdenar={onOrdenar} />
              <th scope="col" className="px-3 py-3 font-medium" title="Status da conta e modelo de cobrança da comissão">
                Status · cobrança
              </th>
              <Cabecalho coluna="recuperado" ordem={ordem} onOrdenar={onOrdenar} />
              <Cabecalho coluna="comissao" ordem={ordem} onOrdenar={onOrdenar} />
              <Cabecalho coluna="taxa" ordem={ordem} onOrdenar={onOrdenar} />
              <th scope="col" className="px-3 py-3 font-medium" title="Recuperado por semana dentro da janela do cabeçalho">
                Tendência
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => {
              const c = linha.company;
              const m = linha.item?.metrics ?? null;
              const anterior = linha.item?.previous ?? null;
              const motivo = linha.saude === null ? null : achadoPrincipal(linha.saude);
              const status = STATUS_EMPRESA[c.status];
              const modelo = MODELO_COBRANCA[c.billingModel];
              return (
                <tr key={c.id} className="border-b border-line/60 transition-colors last:border-0 hover:bg-ink/5 focus-within:bg-ink/5">
                  <th scope="row" className="px-5 py-3 text-left font-normal">
                    <div className="flex items-center gap-3">
                      <CompanyLogo company={c} size="md" />
                      <div className="min-w-0">
                        <Link to={linkDe(c.id)} className="block max-w-[16rem] truncate font-medium text-ink hover:text-brand">
                          {c.name}
                        </Link>
                        <span className="block max-w-[16rem] truncate text-xs text-ink-muted">{c.segment === '' ? 'Segmento não informado' : c.segment}</span>
                        <Provedores linha={linha} />
                      </div>
                    </div>
                  </th>
                  <td className="px-3 py-3">
                    <SeloDeSaude saude={linha.saude} />
                    {motivo === null ? null : <span className="mt-1 block max-w-[14rem] truncate text-[11px] text-ink-subtle" title={motivo.texto}>{motivo.texto}</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className="flex flex-col items-start gap-1">
                      <Badge tone={status.tone} dot>
                        {status.label}
                      </Badge>
                      <span title={`Modelo de cobrança: ${modelo.label}`}>
                        <Badge tone={modelo.tone}>{modelo.curto}</Badge>
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="block font-medium text-ink">{m === null ? VALOR_AUSENTE : formatBRLFromCents(m.recoveredAmountCents)}</span>
                    {m === null ? null : <Delta atual={m.recoveredAmountCents} anterior={anterior === null ? null : anterior.recoveredAmountCents} tipo="dinheiro" compacto />}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="block text-ink">{m === null ? VALOR_AUSENTE : formatBRLFromCents(m.commissionCents)}</span>
                    <span className="block text-xs text-ink-subtle" title={TITULO_PERCENTUAL}>
                      {formatPercentFromBps(c.feeBps)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="block text-ink">{taxaDe(linha)}</span>
                    {m === null || m.abandonedCount === 0 ? null : (
                      <Delta atual={m.recoveryRateBps} anterior={anterior === null || anterior.abandonedCount === 0 ? null : anterior.recoveryRateBps} tipo="bps" compacto />
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Tendencia linha={linha} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="secondary" size="sm" icon="painel" onClick={() => onAbrirCrm(c.id)} aria-label={`Abrir CRM de ${c.name}`}>
                        CRM
                      </Button>
                      <LinkButton to={linkDe(c.id)} variant="ghost" size="sm" iconRight="seta-dir" keepSearch={false}>
                        Detalhes<span className="sr-only"> de {c.name}</span>
                      </LinkButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-elevated text-xs">
              <th scope="row" colSpan={3} className="px-5 py-3 text-left font-medium text-ink-muted">
                Total do recorte ({formatCount(recorte.length)} {recorte.length === 1 ? 'empresa' : 'empresas'}, todas as páginas)
              </th>
              <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(soma.recuperado)}</td>
              <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(soma.comissao)}</td>
              <td className="px-3 py-3 text-right tabular-nums text-ink-muted" title="Vendas recuperadas sobre abandonos do recorte">
                {formatRatio(soma.vendas, soma.abandonos)}
              </td>
              <td className="px-3 py-3" colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      <ul className="flex flex-col gap-3 p-4 md:hidden">
        {linhas.map((linha) => {
          const c = linha.company;
          const m = linha.item?.metrics ?? null;
          const anterior = linha.item?.previous ?? null;
          const status = STATUS_EMPRESA[c.status];
          return (
            <li key={c.id} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4">
              <div className="flex items-start gap-3">
                <CompanyLogo company={c} size="md" />
                <div className="min-w-0 flex-1">
                  <Link to={linkDe(c.id)} className="block truncate font-medium text-ink hover:text-brand">
                    {c.name}
                  </Link>
                  <Provedores linha={linha} />
                </div>
                <SeloDeSaude saude={linha.saude} />
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <dt className="text-xs text-ink-subtle">Recuperado</dt>
                  <dd className="font-medium tabular-nums text-ink">{m === null ? VALOR_AUSENTE : formatBRLFromCents(m.recoveredAmountCents)}</dd>
                  {m === null ? null : (
                    <dd>
                      <Delta atual={m.recoveredAmountCents} anterior={anterior === null ? null : anterior.recoveredAmountCents} tipo="dinheiro" compacto />
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="text-xs text-ink-subtle" title={TITULO_PERCENTUAL}>
                    Comissão · {formatPercentFromBps(c.feeBps)}
                  </dt>
                  <dd className="tabular-nums text-ink">{m === null ? VALOR_AUSENTE : formatBRLFromCents(m.commissionCents)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-subtle">Taxa</dt>
                  <dd className="tabular-nums text-ink">{taxaDe(linha)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-subtle">Status</dt>
                  <dd>
                    <Badge tone={status.tone} dot>
                      {status.label}
                    </Badge>
                  </dd>
                </div>
              </dl>
              <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
                <Button variant="secondary" size="sm" icon="painel" onClick={() => onAbrirCrm(c.id)}>
                  Abrir CRM<span className="sr-only"> de {c.name}</span>
                </Button>
                <LinkButton to={linkDe(c.id)} variant="ghost" size="sm" iconRight="seta-dir" keepSearch={false}>
                  Detalhes<span className="sr-only"> de {c.name}</span>
                </LinkButton>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-line px-5 py-3">
        {recorte.length > tamanhoPagina ? (
          <Pagination total={recorte.length} limit={tamanhoPagina} offset={offset} onChange={onOffset} />
        ) : (
          <p className="text-xs text-ink-subtle">
            {formatCount(recorte.length)} {recorte.length === 1 ? 'empresa' : 'empresas'} numa página só. A partir de {formatCount(tamanhoPagina + 1)} a tabela pagina.
          </p>
        )}
      </div>
    </section>
  );
}
